/**
 * KebabMenu, Stripe-style row action dropdown (•••) for table rows.
 *
 * Replaces visible button-per-row clutter with a single ••• per row that
 * opens a small menu. Click-outside dismisses.
 *
 * Push #48 fix (Ean UAT 2026-06-11): menu is now portal-rendered with
 * fixed positioning so it ESCAPES the overflow:hidden ancestors that
 * .table-wrap and .setup-shell impose for their rounded-corner clipping.
 * Before this fix, clicking the ⋯ on /admin/people opened the menu but
 * the dropdown was clipped to a 0-height sliver, making it look like
 * the menu wasn't opening at all (and any toast firing nearby got
 * blamed on the kebab). Position is recomputed on scroll/resize while
 * open so the menu tracks its trigger.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface KebabAction {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

interface KebabMenuProps {
  actions: KebabAction[];
  /** Optional aria-label for the trigger button (default "Row actions") */
  triggerLabel?: string;
}

interface MenuRect {
  top: number;
  left: number;
}

const MENU_MIN_WIDTH = 180;
const MENU_GAP = 4;

export function KebabMenu({ actions, triggerLabel = 'Row actions' }: KebabMenuProps) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<MenuRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  function computeRect(): MenuRect | null {
    const btn = triggerRef.current;
    if (btn === null) return null;
    const r = btn.getBoundingClientRect();
    // Right-align to the trigger; keep at least 8px from the viewport edge.
    let left = r.right - MENU_MIN_WIDTH;
    if (left < 8) left = 8;
    return { top: r.bottom + MENU_GAP, left };
  }

  // Recompute on open + on every scroll/resize while open so the menu
  // tracks the trigger if the user scrolls the page underneath.
  useLayoutEffect(() => {
    if (!open) return;
    setRect(computeRect());
    function handle() {
      setRect(computeRect());
    }
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [open]);

  // Click-outside + Escape dismiss.
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  if (actions.length === 0) return null;

  return (
    <div className="kebab-wrap">
      <button
        ref={triggerRef}
        type="button"
        className="kebab"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={triggerLabel}
      >
        <DotsIcon />
      </button>
      {open && rect !== null &&
        createPortal(
          <div
            ref={menuRef}
            className="kebab-menu"
            role="menu"
            style={{ position: 'fixed', top: rect.top, left: rect.left, right: 'auto' }}
          >
            {actions.map((a) => (
              <button
                key={a.label}
                type="button"
                role="menuitem"
                className={`kebab-item${a.danger ? ' danger' : ''}`}
                disabled={a.disabled}
                onClick={() => {
                  setOpen(false);
                  a.onSelect();
                }}
              >
                {a.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

function DotsIcon(): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="3" cy="8" r="1.5" />
      <circle cx="8" cy="8" r="1.5" />
      <circle cx="13" cy="8" r="1.5" />
    </svg>
  );
}
